// chat/actions.ts — the chat control executor (cloned from Oriora Expense's src/chat/actions.ts,
// adapted to THIS app's store). The client router (chat/brain.ts) maps ONE user message → ONE
// ChatAction; this file EXECUTES that action against the on-device store (src/store.ts). Everything
// here is LOCAL SQLite over Tauri IPC — no network, no billing — EXCEPT `recap`, which reuses the
// app's ONE existing live endpoint (recapStash) exactly as the RecapTab does. No new server routes.
//
// HITL discipline (mirrors Expense): the destructive actions (delete_skein / delete_pattern) are
// NEVER executed from a chat turn on their own — the Chat UI holds them and requires an explicit
// Confirm tap first.

import {
  store, todayLocal, usableYardage, isPartial, weightLabel, fmtYds,
  type NewSkein, type NewPattern, type PatternStatus, type Skein, type Pattern,
  type SkeinPatch, type PatternPatch, type NewProject, type ProjectPatch, type Project,
} from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "skein";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { recapStash, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact row rendered inline in a chat bubble (filter results). Generic over skeins/patterns.
export interface FilterRow {
  id: string;
  title: string; // primary line
  meta: string;  // secondary line
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_skein"; input: NewSkein }
  | { kind: "add_pattern"; input: NewPattern }
  | { kind: "set_pattern_status"; id: string; status: PatternStatus; label?: string } // updatePattern
  | { kind: "edit_skein"; id: string; patch: SkeinPatch; label?: string }     // general yarn-line edit
  | { kind: "edit_pattern"; id: string; patch: PatternPatch; label?: string } // general pattern edit (beyond status)
  | { kind: "delete_skein"; id: string; label?: string }   // UI confirms before this reaches executeAction
  | { kind: "delete_pattern"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "filter_skein"; pred: (s: Skein) => boolean; label?: string }
  | { kind: "filter_pattern"; pred: (p: Pattern) => boolean; label?: string }
  // ── finished-projects log (the same records the Projects tab lets the user add/edit/delete) ──
  | { kind: "add_project"; input: NewProject }
  | { kind: "edit_project"; id: string; patch: ProjectPatch; label?: string }
  | { kind: "delete_project"; id: string; label?: string } // UI confirms
  | { kind: "filter_project"; pred: (p: Project) => boolean; label?: string }
  | { kind: "counter_new"; name?: string; step?: number }              // create a row counter
  | { kind: "counter_bump"; name?: string; by?: number }               // add rows to a counter (by name, default the only/first)
  | { kind: "counter_reset"; name?: string }                           // reset a counter to 0
  | { kind: "recap" }      // reuses the existing recapStash endpoint (whole-stash recap)
  | { kind: "summarize" } // local deterministic stats — free
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;    // shown back in the chat
  changed?: boolean;  // the stash changed → the main list should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

function skeinRow(s: Skein): FilterRow {
  const title = `${s.brand || "Unnamed"}${s.colorway ? ` · ${s.colorway}` : ""}`;
  const bits: string[] = [];
  if (s.fiber) bits.push(s.fiber);
  if (s.weight) bits.push(weightLabel(s.weight));
  bits.push(`${fmtYds(usableYardage(s))} yds usable`);
  if (isPartial(s)) bits.push("partial");
  return { id: s.id, title, meta: bits.join(" · ") };
}

function patternRow(p: Pattern): FilterRow {
  const bits: string[] = [];
  if (p.yarn_weight) bits.push(weightLabel(p.yarn_weight));
  if (p.total_yardage) bits.push(`${fmtYds(p.total_yardage)} yds`);
  bits.push(p.status);
  return { id: p.id, title: p.name || "Untitled pattern", meta: bits.join(" · ") };
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_skein": {
        const s = await store.createSkein(a.input);
        const cw = s.colorway ? ` · ${s.colorway}` : "";
        return { ok: true, message: `Added ${s.brand}${cw} to your stash.`, changed: true };
      }

      case "add_pattern": {
        const p = await store.createPattern(a.input);
        return { ok: true, message: `Queued ${p.name}.`, changed: true };
      }

      case "set_pattern_status":
        await store.updatePattern(a.id, { status: a.status });
        return { ok: true, message: `Updated ${a.label ?? "the pattern"}.`, changed: true };

      case "edit_skein":
        await store.updateSkein(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the yarn"}.`, changed: true };

      case "edit_pattern":
        await store.updatePattern(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the pattern"}.`, changed: true };

      case "add_project": {
        const pr = await store.createProject(a.input);
        return { ok: true, message: `Logged project "${pr.name}".`, changed: true };
      }
      case "edit_project":
        await store.updateProject(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the project"}.`, changed: true };
      case "delete_project":
        await store.deleteProject(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the project"}.`, changed: true };
      case "filter_project": {
        const rows = (await store.listProjects()).filter(a.pred);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        return {
          ok: true,
          message: `${rows.length} project${rows.length !== 1 ? "s" : ""}${forLabel}` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rows.slice(0, 50).map((pr) => ({
            id: pr.id,
            title: pr.name || "Untitled project",
            meta: [pr.yarn_weight ? weightLabel(pr.yarn_weight) : null, pr.yardage_used ? `${fmtYds(pr.yardage_used)} yds` : null, pr.finished_on || null].filter(Boolean).join(" · "),
          })),
        };
      }

      case "delete_skein":
        await store.deleteSkein(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the yarn"}.`, changed: true };

      case "delete_pattern":
        await store.deletePattern(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the pattern"}.`, changed: true };

      case "filter_skein": {
        const rows = (await store.listSkeins()).filter(a.pred);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        return {
          ok: true,
          message: `${rows.length} yarn line${rows.length !== 1 ? "s" : ""}${forLabel}` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rows.slice(0, 50).map(skeinRow),
        };
      }

      case "filter_pattern": {
        const rows = (await store.listPatterns()).filter(a.pred);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        return {
          ok: true,
          message: `${rows.length} pattern${rows.length !== 1 ? "s" : ""}${forLabel}` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rows.slice(0, 50).map(patternRow),
        };
      }

      case "counter_new": {
        const c = await store.createCounter({ name: a.name, step: a.step });
        return { ok: true, message: `Started a row counter "${c.name}"${c.step !== 1 ? ` (counts by ${c.step})` : ""}. It's in the Row counters section.`, changed: true };
      }
      case "counter_bump": {
        const all = await store.listCounters();
        if (all.length === 0) return { ok: false, message: 'You have no counters yet — say "start a counter called rows" first.' };
        const target = a.name
          ? all.find((c) => c.name.toLowerCase().includes(a.name!.toLowerCase())) ?? null
          : (all.length === 1 ? all[0] : null);
        if (!target) return { ok: false, message: a.name ? `I couldn't find a counter matching "${a.name}".` : "Which counter? You have more than one — name it." };
        const by = a.by ?? target.step;
        const next = Math.max(0, target.count + by);
        await store.setCounterCount(target.id, next);
        return { ok: true, message: `${target.name} is now at ${next}.`, changed: true };
      }
      case "counter_reset": {
        const all = await store.listCounters();
        if (all.length === 0) return { ok: false, message: "You have no counters to reset." };
        const target = a.name
          ? all.find((c) => c.name.toLowerCase().includes(a.name!.toLowerCase())) ?? null
          : (all.length === 1 ? all[0] : null);
        if (!target) return { ok: false, message: a.name ? `I couldn't find a counter matching "${a.name}".` : "Which counter? You have more than one — name it." };
        await store.setCounterCount(target.id, 0);
        return { ok: true, message: `Reset ${target.name} to 0.`, changed: true };
      }

      case "recap": {
        // Reuses the app's ONE live endpoint (recapStash) — same path as RecapTab: compute the
        // deterministic on-device signals first, then send only those aggregates.
        const sig = await store.computeSignals(todayLocal());
        if (sig.totals.skein_lines === 0) {
          return { ok: true, message: "Add a few skeins first — there's no stash to recap yet." };
        }
        const r = await recapStash(sig);
        const recap = r.recap.trim();
        return { ok: true, message: recap || "No recap came back — please try again." };
      }

      case "summarize": {
        const [skeins, patterns, projects] = await Promise.all([
          store.listSkeins(), store.listPatterns(), store.listProjects(),
        ]);
        const usable = skeins.reduce((acc, s) => acc + usableYardage(s), 0);
        const partials = skeins.filter(isPartial).length;
        const queued = patterns.filter((p) => p.status === "queued").length;
        const making = patterns.filter((p) => p.status === "making").length;
        const parts = [
          `${skeins.length} yarn line${skeins.length !== 1 ? "s" : ""}`,
          `${fmtYds(usable)} yds usable`,
          partials ? `${partials} partial${partials !== 1 ? "s" : ""}` : null,
          `${queued} queued${making ? ` + ${making} making` : ""}`,
          `${projects.length} project${projects.length !== 1 ? "s" : ""} finished`,
        ].filter(Boolean);
        return { ok: true, message: parts.join(" · ") + "." };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my stash" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole stash + queue). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    if (e instanceof BrainError) return { ok: false, message: e.message };
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
